/** @file One direction: mode, flipX (authored) or source (mirrored), mirror-asymmetry warnings, the fix. */
import { mirrorAsymmetry } from '@pose2d/format/doc/mirrorAsymmetry';
import type { Dir, DirectionEntry, DirectionsDoc, Doc } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { Button } from '../../ui-kit/Button';
import { Checkbox } from '../../ui-kit/Checkbox';
import { Select } from '../../ui-kit/Select';
import type { RunCommands } from '../rigKit/rigView';
import { directionModes } from './directionModes';
import { FixEditor } from './FixEditor';

/** Props of DirectionEditor. */
export interface DirectionEditorProps {
  doc: Doc;
  t: Translate;
  run: RunCommands;
  set: DirectionsDoc;
  dir: Dir;
}

type SetMode = (input: Record<string, unknown>) => void;

/** flipX for an authored direction, "Mirrors <source>" for a mirrored one. */
function ModeLine({ t, entry, setMode }: { t: Translate; entry: DirectionEntry | undefined; setMode: SetMode }) {
  if (entry !== undefined && entry.mode !== 'authored') return <p className="rig-note">{t('panels.directions.mirrors', { source: entry.source ?? '' })}</p>;
  return <Checkbox label={t('panels.directions.flipX')} checked={entry?.flipX ?? false} onChange={(flipX) => { setMode({ mode: 'authored', flipX }); }} />;
}

/** The MIRROR_ASYMMETRY attachments of `dir` (format's mirrorAsymmetry), with mirrorFix offered to a plain mirror (§5.8). */
function Asymmetry({ doc, t, dir, mirror, setMode }: { doc: Doc; t: Translate; dir: Dir; mirror: boolean; setMode: SetMode }) {
  const sided = mirrorAsymmetry(doc).filter((w) => w.params?.dir === dir).map((w) => String(w.params?.attachment ?? ''));
  if (sided.length === 0) return null;
  return (
    <div className="rig-row">
      <p className="rig-hint">{t('panels.directions.asymmetry', { names: sided.join(', ') })}</p>
      {mirror && <Button label={t('panels.directions.useFix')} onClick={() => { setMode({ mode: 'mirrorFix' }); }} />}
    </div>
  );
}

/** direction.setMode (the source defaults to the counterpart); mirror modes are disabled where the command would refuse them. */
export function DirectionEditor({ doc, t, run, set, dir }: DirectionEditorProps) {
  const entry = set.entries[dir];
  const mode = entry?.mode ?? 'authored';
  const mirrorOk = directionModes.canMirror(set, dir);
  const options = directionModes.MODES.map((m) => ({ value: m, label: t(directionModes.label(m)), disabled: m !== 'authored' && m !== mode && !mirrorOk }));
  const setMode: SetMode = (input) => { run([{ name: 'direction.setMode', input: { dir, ...input } }]); };
  return (
    <fieldset className="rig-fields">
      <legend>{dir}</legend>
      <Select label={t('panels.directions.mode')} value={mode} options={options} onChange={(next) => { setMode({ mode: next }); }} />
      <ModeLine t={t} entry={entry} setMode={setMode} />
      <Asymmetry doc={doc} t={t} dir={dir} mirror={mode === 'mirror'} setMode={setMode} />
      {mode === 'mirrorFix' && <FixEditor doc={doc} t={t} run={run} dir={dir} fix={entry?.fix} />}
    </fieldset>
  );
}
